import { clamp } from '@ikegps/core-measurement';
import {
	IkeCloneable,
	type IkeCollectorType,
	IkeFieldType,
	type IkeProviderCustom,
	type SubFormAnchorTypeState,
	type SubFormSpanTypeState,
	type SubFormEquipmentTypeState,
	SubFormType,
} from '@ikegps/core-api';
import { IkeId } from '../../../id';
import { CollectionModelFieldSubForm } from '../../collection/fields/form.field';
import type { CollectionModelFieldSubFormInstance } from '../../collection/collection.base';
import { FormModelField, type FormModelFieldState, FormModelMutatorField, pruneFieldStateForType } from '../form.field';
import { FormModelFieldLine } from '../form.line';
import { FormModelFieldSelectList } from '../form.select.list';
import { FormModelFieldNestedList } from '../nested.list/form.nested.list';
import { SpanSubFormMutator } from '.././subform/span/form.subform.span.mutator';
import { SpanSubFormValidator } from './span/span.subform.validator';
import { AnchorSubFormMutator, EquipmentSubFormMutator } from '../..';

const TITLE_FIELD_TYPES: IkeFieldType[] = [
	IkeFieldType.TEXT,
	IkeFieldType.NUMBER,
	IkeFieldType.SELECT_LIST,
	IkeFieldType.NESTED_LIST,
];

const SITE_TYPE_FIELD: string = 'Site Type';
const DROP_POLE_FIELD: string = 'Drop Pole';
const POLE_LOADING_FIELD: string = 'Loading Percentage';

const SPAN_COLORS = [
	'#03a8a0',
	'#f16623',
	'#21409a',
	'#ff0984',
	'#66d313',
	'#f44546',
	'#039c4b',
	'#04adff',
	'#e48873',
	'#fedf17',
] as const;

export interface FormFieldCreationOptions {
	displayName?: string;
	id?: string;
	title?: boolean;
	subtitle?: boolean;
	adminOnly?: boolean;
	collector?: IkeCollectorType;
	cloneable?: IkeCloneable;
	providers?: IkeProviderCustom[];
	required?: boolean;
	annotationLabelFieldId?: string;
	clearanceThresholdInMeters?: number;
	locked?: boolean;
	isGenerated?: boolean;
}

export interface FormFieldSubFormState extends FormModelFieldState {
	fields: string[];
	label: string[];

	/**
	 * This field is optional for backwards compatibility.
	 * New subform fields should set its value to SubFormType or null.
	 */
	subFormType?: SubFormType | null;

	/**
	 * If the subFormType is SPAN, then this field must be set.
	 * Contains state only defined for SPAN SubForm fields.
	 */
	spanTypeState?: SubFormSpanTypeState;

	/**
	 * If the subFormType is ANCHOR, then this field must be set.
	 * Contains state only defined for ANCHOR SubForm fields.
	 */
	anchorTypeState?: SubFormAnchorTypeState;

	/**
	 * If the subFormType is ANCHOR, then this field must be set.
	 * Contains state only defined for ANCHOR SubForm fields.
	 */
	equipmentTypeState?: SubFormEquipmentTypeState;
}

export class FormModelMutatorSubForm extends FormModelMutatorField<FormModelFieldSubForm> {
	private getFieldIds() {
		const stateValue = this.field.stateValue;
		if (Array.isArray(stateValue.fields)) {
			return stateValue.fields.slice();
		}
		return [];
	}

	create<T extends FormModelField>(type: IkeFieldType, fieldId?: string, index?: number): T {
		if (fieldId == null || fieldId === '') {
			fieldId = IkeId.generate(10);
		}
		const stateId = this.field.id + ':' + fieldId;
		const fields = this.getFieldIds();
		const isReplace = fields.indexOf(fieldId) > -1;

		if (!isReplace && this.state.getFieldValue(stateId) != null) {
			throw new Error(`FieldId must be unique: ${fieldId}`);
		}

		if (isReplace) {
			// Replacing an existing field's type: preserve only state valid for the new type
			// so flags from the old type (e.g. isAddressLookupField on a now-IMAGE field)
			// don't linger. `existingState` is guaranteed by `isReplace`.
			const existingState = this.state.getFieldValue(stateId)!;
			const newState = pruneFieldStateForType(existingState, type);
			this.state.setFieldValue(stateId, newState);

			// Drop dangling form-level references when the metadata flag that pointed here
			// no longer applies to the new type. Compares fieldId because metadata stores
			// the simple id, which is what this mutator was called with.
			const form = this.field.getForm();
			if (existingState.isStatusField && !newState.isStatusField && form.metadata.statusFieldId === fieldId) {
				form.metadata.setStatusField(null);
			}
			if (existingState.isMapColorField && !newState.isMapColorField && form.metadata.mapColorFieldId === fieldId) {
				form.metadata.setMapColorField(null);
			}
			if (
				existingState.isAddressLookupField &&
				!newState.isAddressLookupField &&
				form.metadata.addressLookupFieldId === fieldId
			) {
				form.metadata.setAddressLookupField(null);
			}
		} else {
			// New field: add to the fields array at the requested position and initialize state.
			if (index == null || index < 0) {
				fields.push(fieldId);
			} else {
				fields.splice(index + 1, 0, fieldId);
			}
			this.state.setFieldValue(this.field.fieldId, { ...this.field.stateValue, fields });
			this.state.setFieldValue(stateId, { type, id: fieldId, displayName: '' });
		}

		// Clear the cached field object so getField constructs the correct class for the new type.
		delete this.field['_fields'][fieldId];

		return this.field.getField(fieldId);
	}

	remove(fieldId: string) {
		if (!this.field.stateValue) {
			// This SubForm field has been deleted so there is nothing to delete.
			return;
		}

		const fields = this.getFieldIds();

		const index = fields.indexOf(fieldId);
		if (index === -1) {
			throw new Error(`Cannot remove field. "${fieldId}" is missing from form "${this.field.id}"`);
		}
		fields.splice(index, 1);

		// Clear metadata references if this field is referenced.
		const form = this.field.getForm();
		if (form.metadata.statusFieldId === fieldId) {
			form.metadata.setStatusField(null);
		}
		if (form.metadata.mapColorFieldId === fieldId) {
			form.metadata.setMapColorField(null);
		}
		if (form.metadata.addressLookupFieldId === fieldId) {
			form.metadata.setAddressLookupField(null);
		}

		// Remove the field from this SubForm's list of owned fields.
		this.state.setFieldValue(this.field.fieldId, { ...this.field.stateValue, fields });

		// Remove the field's state.
		const stateId = this.field.id + ':' + fieldId;
		this.state.removeField(stateId);

		// Remove the field from this SubForm's field cache.
		delete this.field['_fields'][fieldId];
	}

	/**
	 * Move a field in a direction
	 *
	 * @param fieldId Id of field to move
	 * @param direction direction to move +1 Down, -1 Up
	 * @returns Index of field after move
	 */
	move(fieldId: string, direction: number): number {
		const fields = this.getFieldIds();

		const index = fields.indexOf(fieldId);
		if (index === -1) {
			throw new Error(`Cannot move field. "${fieldId}" is missing from form "${this.field.id}"`);
		}
		const nextPosition = clamp(index + direction, 0, fields.length - 1);
		fields.splice(index, 1);
		fields.splice(nextPosition, 0, fieldId);

		this.state.setFieldValue(this.field.fieldId, { ...this.field.stateValue, fields });

		return nextPosition;
	}

	label(label: string[]) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			label,
		});
	}

	/**
	 * Set a subform admin only state
	 *
	 * This functions overrides the standard adminOnly function in order to set all children adminOnly if a subform adminOnly parameter is updated
	 * @param adminOnly adminOnly parameter value
	 */
	override adminOnly(adminOnly: boolean) {
		super.adminOnly(adminOnly);

		for (let field of this.field.fields) {
			field.Mutator.adminOnly(adminOnly);
		}
	}

	/**
	 * Set a subform collector state
	 *
	 * This functions overrides the standard collector function in order to set all children collector if a subform collector parameter is updated
	 * @param adminOnly adminOnly parameter value
	 */
	override collector(collector: IkeCollectorType) {
		super.collector(collector);

		for (let field of this.field.fields) {
			field.Mutator.collector(collector);
		}
	}

	private _span: SpanSubFormMutator | null = null;
	get span(): SpanSubFormMutator {
		if (this._span == null) {
			this._span = new SpanSubFormMutator(this.field);
		}
		return this._span;
	}

	private _anchor: AnchorSubFormMutator | null = null;
	get anchor(): AnchorSubFormMutator {
		if (this._anchor == null) {
			this._anchor = new AnchorSubFormMutator(this.field);
		}
		return this._anchor;
	}

	private _equipment: EquipmentSubFormMutator | null = null;
	get equipment(): EquipmentSubFormMutator {
		if (this._equipment == null) {
			this._equipment = new EquipmentSubFormMutator(this.field);
		}
		return this._equipment;
	}

	/**
	 * Clear the SubForm type.
	 */
	public clearType() {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: null,
			spanTypeState: undefined,
			anchorTypeState: undefined,
		});
	}

	public poleType() {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.POLE,
		} satisfies FormFieldSubFormState);
	}
}

export class FormModelFieldSubForm extends FormModelField<FormFieldSubFormState> {
	static isSubForm(field: FormModelField): field is FormModelFieldSubForm {
		return field.type === IkeFieldType.FORM;
	}

	private _SFMutator: FormModelMutatorSubForm | null = null;
	override get Mutator(): FormModelMutatorSubForm {
		if (this._SFMutator == null) {
			this._SFMutator = new FormModelMutatorSubForm(this);
		}
		return this._SFMutator;
	}

	private _spanValidator: SpanSubFormValidator | null = null;
	get spanValidator(): SpanSubFormValidator {
		if (this._spanValidator == null) {
			this._spanValidator = new SpanSubFormValidator(this);
		}
		return this._spanValidator;
	}

	private _fields: { [fieldId: string]: FormModelField } = {};

	get label() {
		return this.stateValue.label || [];
	}

	get fields(): FormModelField[] {
		const stateValue = this.stateValue;
		if (stateValue.fields == null) {
			return [];
		}
		return stateValue.fields.map((fieldId) => this.getField(fieldId));
	}

	get getFieldsRecursive(): FormModelField[] {
		const stateValue = this.stateValue;
		if (stateValue.fields == null) {
			return [];
		}

		return stateValue.fields.flatMap((fieldId) => {
			const field = this.getField(fieldId);

			if (FormModelFieldSubForm.isSubForm(field)) {
				return [field, ...field.getFieldsRecursive];
			} else {
				return field;
			}
		});
	}

	/**
	 * @argument fieldId The direct ID of the field (without ":" in it).
	 */
	getField<T extends FormModelField>(fieldId: string): T {
		let existing = this._fields[fieldId];
		if (existing != null) {
			return existing as T;
		}
		if (this.stateValue == null || this.stateValue.fields == null) {
			throw new Error(
				`Cannot get field "${fieldId}" because parent field "${this.fieldId}" has no state (it may have been deleted)`
			);
		}
		const fieldIndex = this.stateValue.fields.indexOf(fieldId);
		if (fieldIndex === -1) {
			throw new Error(
				`Cannot get field "${fieldId}" because parent field "${this.fieldId}" does not have that fieldId in its list of owned fields`
			);
		}

		let fieldState = this.getForm().state.getFieldValue(this.id + ':' + fieldId);
		if (fieldState == null) {
			throw new Error('Unable to find field: ' + this.id + ':' + fieldId);
		}

		existing = this.instantiateField(fieldState);
		this._fields[fieldId] = existing;

		return existing as T;
	}

	/**
	 * Returns the field with the given ID, or null if the field does not exist.
	 * @argument fieldId The direct ID of the field (without ":" in it).
	 */
	getOptionalField<T extends FormModelField>(fieldId: string): T | null {
		const existing = this._fields[fieldId];
		if (existing != null) {
			return existing as T;
		}
		if (this.stateValue == null || this.stateValue.fields == null) {
			return null;
		}
		const fieldIndex = this.stateValue.fields.indexOf(fieldId);
		if (fieldIndex === -1) {
			return null;
		}

		const fieldState = this.getForm().state.getFieldValue(this.id + ':' + fieldId);
		if (fieldState == null) {
			return null;
		}

		const field = this.instantiateField(fieldState);
		this._fields[fieldId] = field;

		return field as T;
	}

	private instantiateField(fieldState: FormModelFieldState): FormModelField {
		switch (fieldState.type) {
			case IkeFieldType.FORM:
				return new FormModelFieldSubForm(this, fieldState.id);
			case IkeFieldType.SELECT_LIST:
				return new FormModelFieldSelectList(this, fieldState.id);
			case IkeFieldType.NESTED_LIST:
				return new FormModelFieldNestedList(this, fieldState.id);
			case IkeFieldType.LINE:
				return new FormModelFieldLine(this, fieldState.id);
			default:
				return new FormModelField(this, fieldState.id);
		}
	}

	getAllNestedChildrenFieldsOfType(type: IkeFieldType) {
		const fields: FormModelField[] = [];

		for (const field of this.fields) {
			if (field.type === type) {
				fields.push(field);
			}

			if (FormModelFieldSubForm.isSubForm(field)) {
				fields.push(...field.getAllNestedChildrenFieldsOfType(type));
			}
		}

		return fields;
	}

	get titleFields(): FormModelField[] {
		if (this.label == null || this.label.length === 0) {
			return this.titleField ? [this.titleField] : [];
		}
		return this.label.map((f) => this.getField(f));
	}

	get titleField() {
		const fields = this.fields;

		const assignedTitleFields = fields.filter((field) => field.title);
		if (assignedTitleFields.length > 0) {
			return assignedTitleFields[0];
		}

		const candidateTitleFields = fields.filter((field) => TITLE_FIELD_TYPES.indexOf(field.type) !== -1);

		return candidateTitleFields[0];
	}

	get subtitleField() {
		const fields = this.fields;

		const assignedTitleFields = fields.filter((field) => field.subtitle);
		if (assignedTitleFields.length > 0) {
			return assignedTitleFields[0];
		}

		return null;
	}

	get siteTypeField() {
		const fields = this.fields;

		const siteTypeField = fields.filter((field) => field.displayName === SITE_TYPE_FIELD);
		if (siteTypeField.length > 0) {
			return siteTypeField[0];
		}

		return null;
	}

	get dropPoleField() {
		const fields = this.fields;

		const dropPoleField = fields.filter((field) => field.displayName === DROP_POLE_FIELD);
		if (dropPoleField.length > 0) {
			return dropPoleField[0];
		}

		return null;
	}

	get poleLoadingField() {
		const fields = this.fields;
		const poleLoadingField = fields.filter((field) => field.displayName === POLE_LOADING_FIELD);
		if (poleLoadingField.length > 0) {
			return poleLoadingField[0];
		}
		return null;
	}

	hasField(fieldId: string): boolean {
		// Use nullish access on stateValue in case this field has been deleted.
		return this.stateValue?.fields.includes(fieldId) ?? false;
	}

	/** Get the index of the provided field */
	getFieldIndex(fieldId: string): number {
		return this.stateValue.fields.indexOf(fieldId);
	}

	get subFormType(): SubFormType | null {
		return this.stateValue.subFormType ?? null;
	}

	get subFormSpanTypeState(): SubFormSpanTypeState | null {
		return this.stateValue.spanTypeState ?? null;
	}

	get subFormAnchorState(): SubFormAnchorTypeState | null {
		return this.stateValue.anchorTypeState ?? null;
	}

	get subFormEquipmentState(): SubFormEquipmentTypeState | null {
		return this.stateValue.equipmentTypeState ?? null;
	}

	get typedStateFieldIds(): string[] | null {
		switch (this.subFormType) {
			case SubFormType.SPAN:
				return this.subFormSpanTypeState != null
					? Object.values(this.subFormSpanTypeState).filter((v): v is string => v != null)
					: [];
			case SubFormType.ANCHOR:
				return this.subFormAnchorState != null
					? Object.values(this.subFormAnchorState).filter((v): v is string => v != null)
					: [];
			case SubFormType.EQUIPMENT:
				return this.subFormEquipmentState != null
					? Object.values(this.subFormEquipmentState).filter((v): v is string => v != null)
					: [];
			default:
				return null;
		}
	}

	/**
	 * Search for a field in all children sub forms
	 */
	findField<T extends FormModelField>(fieldId: string): T | null {
		const fields = this.fields;

		for (const field of fields) {
			if (field.id === fieldId) {
				return field as T;
			}

			if (FormModelFieldSubForm.isSubForm(field)) {
				const output = field.findField(fieldId);
				if (output != null) {
					return output as T;
				}
			}
		}
		return null;
	}

	createField<T extends FormModelField>(
		type: IkeFieldType,
		currentOptions: FormFieldCreationOptions = {},
		index?: number
	): T {
		const field: T = this.Mutator.create(type, currentOptions.id, index);

		if (currentOptions.displayName != null) {
			field.Mutator.displayName(currentOptions.displayName);
		}

		if (currentOptions.title) {
			field.Mutator.title(currentOptions.title);
		}

		if (currentOptions.subtitle) {
			field.Mutator.subtitle(currentOptions.subtitle);
		}

		if (currentOptions.adminOnly) {
			field.Mutator.adminOnly(currentOptions.adminOnly);
		}

		if (currentOptions.required) {
			field.Mutator.required(currentOptions.required);
		}

		if (currentOptions.annotationLabelFieldId) {
			field.Mutator.annotationLabelFieldId(currentOptions.annotationLabelFieldId);
		}

		if (currentOptions.clearanceThresholdInMeters) {
			field.Mutator.clearanceThresholdInMeters(currentOptions.clearanceThresholdInMeters);
		}

		if (currentOptions.cloneable != null && currentOptions.cloneable !== IkeCloneable.NONE) {
			field.Mutator.cloneable(currentOptions.cloneable);
		}

		field.Mutator.providers(currentOptions.providers || []);

		if (currentOptions.collector != null) {
			field.Mutator.collector(currentOptions.collector);
		}

		if (currentOptions.locked !== undefined) {
			field.Mutator.setLockState(currentOptions.locked);
		}

		if (currentOptions.isGenerated !== undefined) {
			field.Mutator.isGenerated(currentOptions.isGenerated);
		}

		return field;
	}

	/**
	 * Returns whether a subform is a span subform — either by explicit
	 * subFormType metadata or by name fallback (displayName "span"/"spans").
	 */
	static isSpanType(field: FormModelFieldSubForm): boolean {
		if (field.subFormType === SubFormType.SPAN) {
			return true;
		}
		return ['span', 'spans'].includes(field.displayName.toLowerCase());
	}

	/**
	 * Returns whether a subform is an anchor subform — either by explicit
	 * subFormType metadata or by name fallback (displayName "anchor").
	 */
	static isAnchorType(field: FormModelFieldSubForm): boolean {
		if (field.subFormType === SubFormType.ANCHOR) {
			return true;
		}
		return field.displayName.toLowerCase() === 'anchor';
	}

	/**
	 * Iterates recursively to parent subforms until it finds the root id form and
	 * returns the color of the form if it's span
	 * @param subform subform to get root form id
	 */
	static getSpanColor(subform: CollectionModelFieldSubFormInstance | CollectionModelFieldSubForm): string | null {
		if (subform instanceof CollectionModelFieldSubForm) {
			if (subform.parent.schema.isRoot) {
				const index = subform.parent.parent.value.indexOf(subform.parent) % 10; // get last digit

				return this.isSpanType(subform.parent.schema) ? (SPAN_COLORS[index] ?? null) : null;
			}
			return this.getSpanColor(subform.parent.parent);
		}

		if (subform.parent) {
			if (subform.schema.isRoot) {
				const index = subform.parent.value.indexOf(subform) % 10; // get last digit

				return this.isSpanType(subform.schema) ? (SPAN_COLORS[index] ?? null) : null;
			}
			return this.getSpanColor(subform.parent);
		}
		return null;
	}
}
